import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  IconButton,
  Grid,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { ImageInspectionRegistry } from '../../models/property';
import propertyService from '../../services/propertyService';

interface PropertyAddInspectionImageUploadProps {
  propertyId: string;
  registry: ImageInspectionRegistry | null;
  onRegistryUpdate: () => void; // Callback to refresh registry
}

const PropertyAddInspectionImageUpload: React.FC<PropertyAddInspectionImageUploadProps> = ({ propertyId, registry, onRegistryUpdate }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [processedCount, setProcessedCount] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedFiles([...selectedFiles, ...Array.from(event.target.files)]);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles?.length === 0) return;

    setProcessing(true);
    setProgressMessage(`0/${selectedFiles?.length} images uploaded...`);
    setProcessedCount(0);

    try {
      for (let i = 0; i < selectedFiles?.length; i++) {
        const file = selectedFiles[i];
        setProgressMessage(`Uploading ${file.name} (${i + 1}/${selectedFiles?.length})...`);
        await propertyService.addPictureToPropertyInspections(propertyId, file);
        setProcessedCount((prev) => prev + 1);
      }
      setProgressMessage('Upload complete.');
      setSelectedFiles([]);
      onRegistryUpdate(); // Trigger parent to refresh registry
    } catch (error) {
      console.error('Error uploading files:', error);
      setProgressMessage('An error occurred during upload. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Add More Pictures
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Current Image Count: {registry?.data?.length || 0}
      </Typography>

      {processing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {progressMessage}
          </Typography>
        </Box>
      )}

      {!processing && (
        <>
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              padding: 2,
              textAlign: 'center',
              mb: 2,
              backgroundColor: '#f9f9f9',
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Drag and drop your images here, or click to select files.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AddPhotoAlternateIcon />}
            >
              Select Files
              <input
                type="file"
                multiple
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {selectedFiles?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Selected Files ({selectedFiles?.length}):
              </Typography>
              <Grid container spacing={2}>
                {selectedFiles?.map((file, index) => (
                  <Grid item xs={4} sm={3} md={2} key={index}>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingBottom: '100%',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundImage: `url(${URL.createObjectURL(file)})`,
                        border: '1px solid #ddd',
                        borderRadius: 2,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: '#fff',
                        color: 'red',
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpload}
            disabled={selectedFiles?.length === 0}
          >
            Upload Selected Files
          </Button>
        </>
      )}
    </Box>
  );
};

export default PropertyAddInspectionImageUpload;
